import { trace } from "@ninebone/util";

class LoadingTips extends HTMLElement {
	#tips = [];                  // Stores tip objects {text: string, images: string[]}
	#currentTipIndex = 0;        // Index of the currently displayed tip
	#currentImageIndex = 0;      // Index of the currently displayed image for the current tip
	#tipIntervalId = null;       // Interval ID for cycling through tips
	#imageIntervalId = null;     // Interval ID for cycling through images within a tip
	#tipDisplayDuration = 10000;  // How long each tip is displayed (5 seconds)
	#imageDisplayDuration = 3000; // How long each image within a tip is displayed (2 seconds)
	#host;

	#cdnBase = `https://cdn.jsdelivr.net/npm/@ninebone/ai@${__APP_VERSION__}/dist`;

	// DOM element references (cached for performance)
	tipElement = null;
	loadingAnimation = null;
	tipImageElement = null;
	tipImageContainer = null;

	constructor() {
		super();
		this.attachShadow({ mode: 'open' });
	}

	connectedCallback() {

		this.#host = this.getRootNode().host.getRootNode().host;

		const customImport = nine.cssPath ? `@import "${nine.cssPath}/nine-ai.css";` : "";

		this.shadowRoot.innerHTML = `
			<style>
				@import "https://cdn.jsdelivr.net/npm/@ninebone/ai@${__APP_VERSION__}/dist/css/nine-ai.css";
				${customImport}
			</style>
            
            <div class="chat-message loading-tips-message">
				<div class="loading-bar-wrapper">
				  <div class="loading-bar"></div>
				</div>
				<div class="tip-content">
					<div class="tip-image-container">
						<img class="tip-image" src="" alt="팁 관련 이미지">
					</div>
				</div>
				<div class="current-tip-wrapper">
					<p class="current-tip"></p>
				</div>
			</div>
        `;

		/**
		 * <div class="loading-animation-wrapper">
		 *                    <div class="loading-animation"></div>
		 *                </div>
		 * @type {Element}
		 */
		// Cache DOM element references
		this.tipElement = this.shadowRoot.querySelector('.current-tip');
		this.loadingAnimation = this.shadowRoot.querySelector('.loading-animation');
		this.loadingAnimationWrapper = this.shadowRoot.querySelector('.loading-animation-wrapper'); // 새롭게 추가된 래퍼 참조
		this.tipImageElement = this.shadowRoot.querySelector('.tip-image');
		this.tipImageContainer = this.shadowRoot.querySelector('.tip-image-container');
	}

	disconnectedCallback() {
		// Clean up all intervals when the component is removed from the DOM
		this.stopTips();
	}

	/**
	 * Loads tips asynchronously from a JSON URL and starts displaying them.
	 * @param {string} tipsUrl - The URL of the JSON file containing tip data.
	 * @param {string} tipCategory - The key in the JSON file's data that corresponds to the desired tip array (e.g., 'componentTips').
	 */
	async startTips(tipsUrl, tipCategory) {

		if (!tipsUrl || !tipCategory) {
			trace.error("Tips URL or category not provided for aiLoadingTips.");
			return;
		}

		try {
			const response = await fetch(tipsUrl);
			if (!response.ok) {
				throw new Error(`Failed to fetch tips: ${response.statusText}`);
			}
			const data = await response.json();
			const tips = data[tipCategory];

			if (!tips || tips.length === 0) {
				trace.warn(`No tips found for category '${tipCategory}' in ${tipsUrl}.`);
				return;
			}

			this.#tips = tips;
			//this.#currentTipIndex = 0;
			this.#currentTipIndex = Math.floor(Math.random() * this.#tips.length);
			this.stopTips(); // Stop any existing intervals before starting new ones

			this.showRandomTip(); // Display the first tip immediately

			// Start interval for cycling through tips
			this.#tipIntervalId = setInterval(() => {
				this.showRandomTip();
			}, this.#tipDisplayDuration);

			//this.loadingAnimation.style.display = 'block'; // Show the loading spinner
			//this.loadingAnimationWrapper.style.display = 'flex';
		} catch (error) {
			trace.error("Error loading tips:", error);
			this.stopTips(); // Stop intervals and hide animations on error
			if (this.tipElement) {
				this.tipElement.textContent = "팁을 불러오는 중 오류가 발생했습니다.";
			}
		}
	}

	/**
	 * Stops all tip and image display intervals and hides related UI elements.
	 */
	stopTips() {
		if (this.#tipIntervalId) {
			clearInterval(this.#tipIntervalId);
			this.#tipIntervalId = null;
		}
		if (this.#imageIntervalId) {
			clearInterval(this.#imageIntervalId);
			this.#imageIntervalId = null;
		}
		//this.loadingAnimationWrapper.style.display = 'none';
		this.tipImageElement.style.display = 'none'; // Hide image
		this.tipImageElement.src = ''; // Clear image source
		this.tipImageContainer.style.display = 'none'; // Hide image container
	}

	/**
	 * Displays the current tip's text and manages its associated images.
	 */
	// aiLoadingTips.js (showCurrentTip 메서드 내부)

	/**
	 * Displays a randomly selected tip (excluding the currently displayed one if possible)
	 * and manages its associated images.
	 */
	showRandomTip() {
		if (this.#tips.length === 0) {
			this.tipElement.textContent = "";
			this.tipImageElement.style.display = 'none';
			this.tipImageContainer.style.display = 'none';
			return;
		}

		let nextIndex = this.#currentTipIndex; // 초기값을 현재 인덱스로 설정
		const maxAttempts = 3; // 최대 시도 횟수

		// 팁이 1개 초과인 경우에만 다른 팁을 찾으려고 시도합니다.
		// 팁이 하나뿐이거나 없으면 아래 루프에 들어가지 않으므로,
		// nextIndex는 초기값인 currentTipIndex (0이 될 가능성 높음)를 유지합니다.
		if (this.#tips.length > 1) {
			for (let i = 0; i < maxAttempts; i++) {
				const potentialNextIndex = Math.floor(Math.random() * this.#tips.length);
				if (potentialNextIndex !== this.#currentTipIndex) {
					nextIndex = potentialNextIndex;
					break; // 다른 팁을 찾으면 루프 종료
				}
			}
		}
		// 이전에 있던 'else { nextIndex = 0; }' 블록이 제거되었습니다.
		// 팁이 1개일 경우, if (this.#tips.length > 1) 조건에 걸리지 않고
		// nextIndex는 그대로 this.#currentTipIndex (대부분 0)를 유지하게 됩니다.

		this.#currentTipIndex = nextIndex; // 최종 결정된 인덱스를 currentTipIndex에 할당

		const currentTip = this.#tips[this.#currentTipIndex];

		if (this.tipElement && currentTip) {
			this.tipElement.innerHTML = currentTip.text;

			if (this.#imageIntervalId) {
				clearInterval(this.#imageIntervalId);
				this.#imageIntervalId = null;
			}

			if (currentTip.images && currentTip.images.length > 0) {
				this.#currentImageIndex = 0;
				this.showCurrentImage(currentTip.images[this.#currentImageIndex]);
				this.tipImageElement.style.display = 'block';
				this.tipImageContainer.style.display = 'flex';

				if (currentTip.images.length > 1) {
					this.#imageIntervalId = setInterval(() => {
						this.#currentImageIndex = (this.#currentImageIndex + 1) % currentTip.images.length;
						this.showCurrentImage(currentTip.images[this.#currentImageIndex]);
					}, this.#imageDisplayDuration);
				}
			} else {
				this.tipImageElement.style.display = 'none';
				this.tipImageContainer.style.display = 'none';
				this.tipImageElement.src = '';
			}
		}
	}

	/**
	 * Sets the source of the image element to display the given image.
	 * @param {string} imageUrl - The URL of the image to display.
	 */
	showCurrentImage(imageUrl) {
		if (this.tipImageElement) {
			this.tipImageElement.src = `${this.#cdnBase}${imageUrl}`;
		}
	}
}

if (!customElements.get("nine-ai-loading-tips")) {
	customElements.define("nine-ai-loading-tips", LoadingTips);
}